import { useRouter } from 'vue-router'

import { useTokenManager } from '@/utils/authorization/token-manager'
import { maLocalStorage, maSessionStorage } from '@/lib/storage'
import { useMaCore } from '@/composables/maCore'
import { maLogger } from '@/base/logger'
import { MAIN_PAGE_ROUTE_NAME, RouteName } from '@/base/constants/route'
import { AUTH_PLATFORM } from '@/base/constants/Auth'

export const AUTH_TYPES = Object.freeze({
  GOOGLE: 'google',
  LINKEDIN: 'linkedin',
  SSO: 'sso',
  APPLE: 'apple',
})

export const AUTH_ACTION = Object.freeze({
  LOGIN: 'login',
  REGISTER: 'register',
  PASSWORD_RESET: 'password-reset',
  VERIFY: 'verify',
  TEAM_MEMBER_INVITE_COMPLETE: 'team-member-invite-complete',
})

/**
 * This file was converted from mixin (`UserLoginMixin`) to composable without changing the logic.
 */
export function useAuthUtils() {
  const router = useRouter()
  const { analytics } = useMaCore()
  const { getRefreshTokenData, setUserAuth } = useTokenManager()

  const getRedirectRouteName = (action) => {
    switch (action) {
      case AUTH_ACTION.REGISTER:
        return { name: RouteName.AUTH_REGISTER_SUCCESS }
      case AUTH_ACTION.VERIFY:
        return { name: RouteName.ONBOARDING }
      default:
        if (maSessionStorage.forwardTo) {
          const forwardRoute = router.resolve(maSessionStorage.forwardTo)
          maSessionStorage.forwardTo = null
          const isValidRoute = forwardRoute.matched.some(route => route.name != RouteName.PATH_MATCH)
          if (isValidRoute)
            return forwardRoute
        }
        return { name: MAIN_PAGE_ROUTE_NAME }
    }
  }

  const isUserLoggedIn = () => {
    const tokenData = getRefreshTokenData()
    const rft = tokenData?.exp ?? 0

    // adding 30 seconds as request flight time so token won't be expired when it arrives (30s is heroku limit)
    const nowInSecs = new Date() / 1000 + 30

    return rft > nowInSecs
  }

  const redirectToSearchAds = () => {
    maSessionStorage.platform = null
    window.location.href = import.meta.env.VITE_APP_SEARCH_ADS_INSIGHTS_DOMAIN
  }

  const checkLoggedInAndRedirect = async (redirectRoute = { name: MAIN_PAGE_ROUTE_NAME }) => {
    if (!isUserLoggedIn())
      return

    if (maSessionStorage.platform === AUTH_PLATFORM.SA && redirectRoute.name !== RouteName.AUTH_REGISTER_SUCCESS) {
      return redirectToSearchAds()
    }

    await router.replace(redirectRoute)
  }

  const processSignInRequest = async (userAuth, action, type) => {
    setUserAuth(userAuth)
    maLocalStorage.loginCsrf = null// login session is completed don't use it anymore

    maLogger.info('Logged in using', type, action, userAuth)
    analytics.login(userAuth.newUser, userAuth.userId, type?.toUpperCase())

    await checkLoggedInAndRedirect(getRedirectRouteName(action))
  }

  const getPlatformName = () => maSessionStorage.platform === AUTH_PLATFORM.SA ? 'SearchAds' : 'MobileAction'
  const getPlatform = () => maSessionStorage.platform || AUTH_PLATFORM.MA

  return {
    isUserLoggedIn,
    checkLoggedInAndRedirect,
    processSignInRequest,
    getPlatformName,
    getPlatform,
    redirectToSearchAds,
  }
}
